import { AI_DUDE_DEFAULT_LOCAL_PORT, AI_DUDE_ENVS } from '@iac/contracts';
import type { AiDudeEnv, AiDudeQuery } from '@iac/contracts';
import { useState, type FormEvent } from 'react';
import { AlertError } from '../../components/Alert';
import { Field } from '../../components/Field';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { useAiDudeEnvironments, useAiDudeStatus } from './api';
import { ag5Dict } from './i18n';

const ENV_STORAGE_KEY = 'ag5.aiDude.env';
const PORT_STORAGE_KEY = 'ag5.aiDude.localPort';

/**
 * The env and local port are remembered per browser for convenience; storage
 * may be unavailable, and then the defaults apply.
 */
function loadStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function saveStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not remembering the choice is fine.
  }
}

function loadEnv(): AiDudeEnv {
  const stored = loadStored(ENV_STORAGE_KEY);
  return AI_DUDE_ENVS.find((e) => e === stored) ?? 'local';
}

function isPort(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 65535;
}

function loadPort(): number {
  const stored = Number(loadStored(PORT_STORAGE_KEY));
  return isPort(stored) ? stored : AI_DUDE_DEFAULT_LOCAL_PORT;
}

/** Applied on submit, not per keystroke, so typing 8000 doesn't probe 8, 80, 800. */
function LocalPortForm({
  port,
  onChange,
}: {
  port: number;
  onChange: (port: number) => void;
}) {
  const t = useDict(ag5Dict);
  const [draft, setDraft] = useState(String(port));

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = Number(draft);
    if (isPort(next)) onChange(next);
  }

  return (
    <form onSubmit={onSubmit} className="toolbar ai-dude-port-form">
      <Field
        id="ai-dude-local-port"
        label={t.localPort}
        name="localPort"
        type="number"
        min={1}
        max={65535}
        step={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        required
      />
      <button type="submit" className="btn-ghost">
        {t.setPort}
      </button>
    </form>
  );
}

/** The AI Dude debugger. Every call goes through our API, never directly. */
export function AiDude() {
  const t = useDict(ag5Dict);
  usePageTitle(t.aiDudeTitle);
  const [env, setEnv] = useState<AiDudeEnv>(loadEnv);
  const [localPort, setLocalPort] = useState(loadPort);
  const query: AiDudeQuery = env === 'local' ? { localPort } : {};
  const { data: environments } = useAiDudeEnvironments();
  const { data: status, isFetching, refetch } = useAiDudeStatus(env, query);
  // The probe reports the URL it actually used, which carries the local port.
  const apiUrl =
    status?.env === env
      ? status.apiUrl
      : environments?.find((e) => e.env === env)?.apiUrl;

  function selectEnv(next: AiDudeEnv) {
    setEnv(next);
    saveStored(ENV_STORAGE_KEY, next);
  }

  function selectPort(next: number) {
    setLocalPort(next);
    saveStored(PORT_STORAGE_KEY, String(next));
  }

  return (
    <>
      <PageHeader title={t.aiDude} />

      <Panel title={t.environment}>
        <div className="toolbar" role="group" aria-label={t.environment}>
          {AI_DUDE_ENVS.map((e) => (
            <button
              key={e}
              type="button"
              className={e === env ? 'btn-toggle is-active' : 'btn-toggle'}
              aria-pressed={e === env}
              onClick={() => selectEnv(e)}
            >
              {e.toUpperCase()}
            </button>
          ))}
        </div>
        {env === 'local' && (
          <LocalPortForm port={localPort} onChange={selectPort} />
        )}
        {apiUrl && (
          <p id="ai-dude-api-url" className="form-hint ai-dude-api-url">
            {t.apiLabel} <code>{apiUrl}</code>
          </p>
        )}

        <div id="ai-dude-status" className="form-result">
          {status &&
            (status.available ? (
              <p id="ai-dude-version" className="form-hint">
                {t.availableVersion} <code>{status.version}</code> ·{' '}
                {status.serviceStatus} · {status.latencyMs} ms
              </p>
            ) : (
              <AlertError message={`${t.unavailable} ${status.error}`} />
            ))}
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            {isFetching ? t.checking : t.checkAgain}
          </button>
        </div>
      </Panel>
    </>
  );
}
